import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { authService, MFARequiredData, AuthUser } from '../services/auth';

interface MFACodeInputProps {
  mfaData: MFARequiredData;
  cognitoUser: CognitoUser;
  onSuccess: (user: AuthUser) => void;
  onCancel: () => void;
}

export const MFACodeInput: React.FC<MFACodeInputProps> = ({
  mfaData,
  cognitoUser,
  onSuccess,
  onCancel
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const user = await authService.submitMFACode(cognitoUser, code);
      onSuccess(user);
    } catch (err: any) {
      setError(err.message || 'Invalid code. Please try again.');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        p: 2
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%'
        }}
      >
        <Typography variant="h4" gutterBottom align="center">
          Two-Factor Authentication
        </Typography>

        <Typography variant="body2" color="text.secondary" align="center" paragraph>
          Enter the 6-digit code from your authenticator app
        </Typography>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Logging in as: <strong>{mfaData.email}</strong>
        </Typography>

        <TextField
          fullWidth
          label="6-Digit Code"
          value={code}
          onChange={handleCodeChange}
          placeholder="000000"
          inputProps={{
            maxLength: 6,
            style: { fontSize: '24px', textAlign: 'center', letterSpacing: '8px' }
          }}
          sx={{ mb: 2 }}
          autoFocus
          onKeyPress={(e) => {
            if (e.key === 'Enter' && code.length === 6) {
              handleSubmit();
            }
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          The code changes every 30 seconds. Make sure to enter the current code.
        </Alert>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            fullWidth
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            fullWidth
            disabled={loading || code.length !== 6}
          >
            {loading ? <CircularProgress size={24} /> : 'Verify'}
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 3, display: 'block' }}>
          Lost your device? Contact your administrator for help.
        </Typography>
      </Paper>
    </Box>
  );
};

